import { oc } from "@orpc/contract";
import { z } from "zod";
import { AddressInput } from "./customer-address";

/**
 * Customer accounts in the public shop API (spec, "Klanten" and "API"): register with email and
 * password, confirm the address, log in and out, reset the password.
 *
 * - No answer says whether an address has an account: registering, asking for a new confirmation
 *   link and asking for a reset all answer 204, whatever the address.
 * - Registering and asking for a mail need a Turnstile token from the shop's widget
 *   (`turnstileToken`); every endpoint that takes an address or a token is rate limited (429).
 * - Links in mail open a page of the shop with `?token=...`, and the page posts that token here:
 *   `/account/bevestigen` to `confirmEmail`, `/account/nieuw-wachtwoord` to `resetPassword`. The
 *   "you already have an account" mail links to `/account/inloggen`.
 * - A signed-in customer is the `__Host-customer-session` cookie the API sets and clears: HttpOnly,
 *   so no token ever reaches script, and separate from the admin's employee session.
 */

/** An email address; spaces around it are dropped. The API compares addresses in lower case. */
export const CustomerEmail = z.string().trim().pipe(z.email().max(254));

/** A password: at least 8 characters. Stored only as a hash. */
export const CustomerPassword = z.string().min(8).max(128);

/** What the shop's Turnstile widget put in the form (`cf-turnstile-response`). */
export const TurnstileToken = z.string().min(1).max(2048);

/** A first or last name, as the customer writes it (`de Vries` keeps its lower case). */
export const PersonName = z.string().trim().min(1).max(100);

/**
 * A phone number, typed any way (`06-1234 5678`, `+32 470 12 34 56`, `(010) 123 45 67`), stored
 * without the spaces, dashes, dots and brackets: digits with an optional leading `+`.
 */
export const PhoneNumber = z
	.string()
	.max(40)
	.transform((value) => value.replace(/[\s().-]/g, ""))
	.pipe(z.string().regex(/^\+?\d{9,15}$/));

/**
 * Registering asks everything checkout needs, all required: name, phone and an address, which
 * becomes the default billing and shipping address in the address book. Company data comes later,
 * with the business application.
 */
export const RegisterInput = z.object({
	email: CustomerEmail,
	password: CustomerPassword,
	firstName: PersonName,
	lastName: PersonName,
	phone: PhoneNumber,
	address: AddressInput,
	turnstileToken: TurnstileToken,
});
export type RegisterInput = z.input<typeof RegisterInput>;
export type RegisterRequest = z.output<typeof RegisterInput>;

/** The token from a link in a mail (`?token=` on the shop page). */
export const LinkToken = z.string().min(1).max(200);

export const ConfirmEmailInput = z.object({ token: LinkToken });
export type ConfirmEmailInput = z.infer<typeof ConfirmEmailInput>;

export const ResendConfirmationInput = z.object({
	email: CustomerEmail,
	turnstileToken: TurnstileToken,
});
export type ResendConfirmationInput = z.infer<typeof ResendConfirmationInput>;

/**
 * Logging in. After 3 failed attempts for the address the API answers `BotCheckRequired` (400)
 * until the login comes with a Turnstile token (#222): the shop then shows the widget.
 */
export const LoginInput = z.object({
	email: CustomerEmail,
	password: z.string().min(1).max(128),
	turnstileToken: TurnstileToken.optional(),
});
export type LoginInput = z.infer<typeof LoginInput>;

/**
 * The price display (CONTEXT.md `vatDisplay`, "prijsweergave"): whether the shop shows a signed-in
 * customer's prices incl. or excl. VAT. Display only: the API prices a customer as it always did
 * (the cart's `priceDisplay`, `vatBasis`), and a consumer's `totals.toPay` stays incl. VAT.
 */
export const VatDisplay = z.enum(["incl", "excl"]);
export type VatDisplay = z.infer<typeof VatDisplay>;

/** The price display that holds (`GET`), or the one the customer chooses (`PUT`). */
export const AccountVatDisplay = z.object({ vatDisplay: VatDisplay });
export type AccountVatDisplay = z.infer<typeof AccountVatDisplay>;

/** The signed-in customer, and when the session ends. */
export const CustomerSession = z.object({
	customer: z.object({ id: z.string(), email: z.string() }),
	/** ISO 8601, UTC. After that the customer logs in again. */
	expiresAt: z.iso.datetime(),
	/**
	 * The price display that holds now: the customer's own choice, or else the default (excl with an
	 * approved business profile, incl for everyone else). The shop renders every price on this side.
	 */
	vatDisplay: VatDisplay,
});
export type CustomerSession = z.infer<typeof CustomerSession>;

export const RequestPasswordResetInput = z.object({
	email: CustomerEmail,
	turnstileToken: TurnstileToken,
});
export type RequestPasswordResetInput = z.infer<typeof RequestPasswordResetInput>;

export const ResetPasswordInput = z.object({
	token: LinkToken,
	password: CustomerPassword,
});
export type ResetPasswordInput = z.infer<typeof ResetPasswordInput>;

/** Where a business application stands: `pending` until an employee approves or rejects it (#80). */
export const BusinessProfileStatus = z.enum(["pending", "approved", "rejected"]);
export type BusinessProfileStatus = z.infer<typeof BusinessProfileStatus>;

/**
 * What VIES said about the VAT id. `unknown`: VIES could not be asked or could not say; the
 * application goes through anyway and an employee looks at it.
 */
export const ViesResult = z.enum(["valid", "invalid", "unknown"]);
export type ViesResult = z.infer<typeof ViesResult>;

/** Drops the spaces, dots and dashes people type in numbers, and upper-cases the letters. */
const compact = (value: string) => value.replace(/[\s.-]/g, "").toUpperCase();

/**
 * A Dutch or Belgian VAT id, typed any way (`nl 1234.56.789 b01`), as it is stored:
 * `NL123456789B01` or `BE0123456789`.
 */
export const VatId = z
	.string()
	.max(40)
	.transform(compact)
	.pipe(z.string().regex(/^(NL\d{9}B\d{2}|BE[01]\d{9})$/));

/** A KvK number (8 digits) or a Belgian KBO number (10 digits), stored as digits only. */
export const KvkNumber = z
	.string()
	.max(40)
	.transform(compact)
	.pipe(z.string().regex(/^(\d{8}|[01]\d{9})$/));

export const BusinessApplicationInput = z.object({
	companyName: z.string().trim().min(1).max(200),
	kvkNumber: KvkNumber,
	vatId: VatId,
	turnstileToken: TurnstileToken,
});
export type BusinessApplicationInput = z.input<typeof BusinessApplicationInput>;
export type BusinessApplicationRequest = z.output<typeof BusinessApplicationInput>;

/**
 * The signed-in customer's business application as the shop shows it: what they sent, where it
 * stands, and what the employee decided. Of VIES the shop only learns whether it found the VAT id
 * valid ("Gecontroleerd via VIES"); the name and address it has are the admin's to see.
 */
export const BusinessApplication = z.object({
	companyName: z.string(),
	kvkNumber: z.string(),
	vatId: z.string(),
	status: BusinessProfileStatus,
	/** ISO 8601. */
	appliedAt: z.iso.datetime(),
	/** VIES found the VAT id valid. False when it found it invalid or could not say. */
	viesChecked: z.boolean(),
	/** Days an invoice on account may stay open; set once approved, else null. */
	paymentTermDays: z.number().int().nullable(),
	/** Why it was rejected, as the mail said; null unless rejected. */
	rejectionReason: z.string().nullable(),
});
export type BusinessApplication = z.infer<typeof BusinessApplication>;

/**
 * "Mijn gegevens": the signed-in customer's own data. Name and phone are null for a customer who
 * registered before they were asked (#72). The email address is the login; changing it comes later.
 */
export const CustomerProfile = z.object({
	email: z.string(),
	/** The display name (CONTEXT.md `name`): first and last name joined; null without either. */
	name: z.string().nullable(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	phone: z.string().nullable(),
});
export type CustomerProfile = z.infer<typeof CustomerProfile>;

/**
 * Changing "Mijn gegevens": any of first name, last name and phone, checked as at registration.
 * A field left out stays as it is.
 */
export const UpdateProfileInput = z
	.object({ firstName: PersonName, lastName: PersonName, phone: PhoneNumber })
	.partial();
export type UpdateProfileInput = z.input<typeof UpdateProfileInput>;
export type UpdateProfileRequest = z.output<typeof UpdateProfileInput>;

export const CustomerAccountContract = {
	/**
	 * Applies for a business account (#79), for the signed-in customer: company name, KvK number
	 * and VAT id, behind Turnstile. The VAT id goes through VIES; VIES being down does not stop the
	 * application. The application is `pending` until an employee looks at it. Errors:
	 * `CustomerNotSignedIn` (401), `BotCheckFailed` (400), `BusinessApplicationExists` (409, one is
	 * pending or approved already; after a rejection the customer may apply again).
	 */
	applyForBusiness: oc
		.route({ method: "POST", path: "/shop/account/business-application" })
		.input(BusinessApplicationInput)
		.output(BusinessApplication),
	/**
	 * The signed-in customer's business application. Errors: `CustomerNotSignedIn` (401),
	 * `BusinessApplicationNotFound` (404, never applied).
	 */
	businessApplication: oc
		.route({ method: "GET", path: "/shop/account/business-application" })
		.output(BusinessApplication),
	/**
	 * Confirms the address with the token from the confirmation mail; the account can log in from
	 * now on. A link works once and for 24 hours: 410 `CustomerLinkInvalid` after that, and for a
	 * link replaced by a newer one.
	 */
	confirmEmail: oc
		.route({ method: "POST", path: "/shop/account/confirm-email", successStatus: 204 })
		.input(ConfirmEmailInput),
	/**
	 * Logs in and sets the customer session cookie. 401 `InvalidCredentials` for a wrong password
	 * and an unknown address alike; 403 `EmailNotConfirmed` (right password, address not confirmed).
	 */
	login: oc.route({ method: "POST", path: "/shop/account/login" }).input(LoginInput).output(CustomerSession),
	/** Ends the session and clears its cookie; fine without a session too. */
	logout: oc.route({ method: "POST", path: "/shop/account/logout", successStatus: 204 }),
	/** The signed-in customer's own data ("Mijn gegevens"). Errors: `CustomerNotSignedIn` (401). */
	profile: oc.route({ method: "GET", path: "/shop/account/profile" }).output(CustomerProfile),
	/**
	 * Changes the signed-in customer's first name, last name and/or phone, checked as at
	 * registration, and answers with the result. Errors: `CustomerNotSignedIn` (401), 400 for a
	 * field that does not pass.
	 */
	updateProfile: oc
		.route({ method: "PATCH", path: "/shop/account/profile" })
		.input(UpdateProfileInput)
		.output(CustomerProfile),
	/**
	 * Registers an account with the customer's name, phone and address (the default billing and
	 * shipping address), and mails a link to confirm the email address. 204 for any address: one
	 * that already has a confirmed account gets a mail saying so instead, and nothing changes; an
	 * unconfirmed account takes the newest registration's password, name, phone and address. 400
	 * `BotCheckFailed` without a good Turnstile token.
	 */
	register: oc
		.route({ method: "POST", path: "/shop/account/register", successStatus: 204 })
		.input(RegisterInput),
	/**
	 * Mails a link to set a new password (one hour, once), replacing an earlier one. 204 for any
	 * address; only an address with an account gets a mail.
	 */
	requestPasswordReset: oc
		.route({ method: "POST", path: "/shop/account/password-reset", successStatus: 204 })
		.input(RequestPasswordResetInput),
	/**
	 * Mails a new confirmation link, which replaces the previous one. 204 for any address; only an
	 * unconfirmed account gets a mail.
	 */
	resendConfirmation: oc
		.route({ method: "POST", path: "/shop/account/confirm-email/resend", successStatus: 204 })
		.input(ResendConfirmationInput),
	/**
	 * Sets the new password with the token from the reset mail, confirms the address (the link
	 * proves it), and ends every session of the account; the customer logs in again. 410
	 * `CustomerLinkInvalid` for a used, expired or replaced link.
	 */
	resetPassword: oc
		.route({ method: "POST", path: "/shop/account/password-reset/complete", successStatus: 204 })
		.input(ResetPasswordInput),
	/** The signed-in customer; 401 `CustomerNotSignedIn` without a live session. */
	session: oc.route({ method: "GET", path: "/shop/account/session" }).output(CustomerSession),
	/**
	 * The price display that holds for the signed-in customer: their own choice, or else the
	 * default (excl with an approved business profile, incl otherwise, also while an application
	 * is pending or after it was rejected). Errors: `CustomerNotSignedIn` (401).
	 */
	vatDisplay: oc.route({ method: "GET", path: "/shop/account/vat-display" }).output(AccountVatDisplay),
	/**
	 * Keeps the customer's choice on their account and answers with what holds now. Display only:
	 * prices, the cart and what is paid do not change. No way back to the default: the customer
	 * chooses incl or excl. Errors: `CustomerNotSignedIn` (401), 400 for any other value.
	 */
	setVatDisplay: oc
		.route({ method: "PUT", path: "/shop/account/vat-display" })
		.input(AccountVatDisplay)
		.output(AccountVatDisplay),
};
